"use client";

import type { ZoteroPaper } from "@/lib/desktop/zotero";
import type { FileInfo } from "@/stores/slices/lib/paperTypes";

const STORAGE_KEY = "findin:zotero-paper-links:v1";
const LINKS_CHANGED_EVENT = "findin:zotero-paper-links-changed";
const zoteroSources = new WeakMap<File, ZoteroPaper>();

export type ZoteroPaperLink = {
  itemKey: string;
  attachmentKey: string;
  md5: string;
  sha1: string;
  linkedAt: number;
};

type StoredLinks = Record<string, ZoteroPaperLink>;

function isZoteroKey(value: unknown): value is string {
  return typeof value === "string" && /^[A-Z0-9]{8}$/.test(value);
}

function isHash(value: unknown, length: number): value is string {
  return typeof value === "string"
    && value.length === length
    && /^[a-f0-9]+$/i.test(value);
}

function isZoteroPaperLink(value: unknown): value is ZoteroPaperLink {
  if (!value || typeof value !== "object") return false;
  const link = value as Partial<ZoteroPaperLink>;
  return isZoteroKey(link.itemKey)
    && isZoteroKey(link.attachmentKey)
    && isHash(link.md5, 32)
    && isHash(link.sha1, 40)
    && typeof link.linkedAt === "number";
}

function readLinks(): StoredLinks {
  if (typeof window === "undefined") return {};
  try {
    const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || "{}") as unknown;
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
    return Object.fromEntries(
      Object.entries(parsed).filter((entry): entry is [string, ZoteroPaperLink] => (
        Boolean(entry[0]) && isZoteroPaperLink(entry[1])
      )),
    );
  } catch {
    return {};
  }
}

export function registerZoteroSource(file: File, paper: ZoteroPaper) {
  zoteroSources.set(file, paper);
}

export function bindZoteroSourceToPaper(
  file: File,
  fileInfo: FileInfo,
  paperId: string | number,
) {
  if (typeof window === "undefined") return false;
  const source = zoteroSources.get(file);
  const normalizedPaperId = String(paperId).trim();
  if (!source || !normalizedPaperId) return false;

  return saveZoteroPaperLink(normalizedPaperId, {
    itemKey: source.itemKey,
    attachmentKey: source.attachmentKey,
    md5: fileInfo.md5.toLowerCase(),
    sha1: fileInfo.sha1.toLowerCase(),
    linkedAt: Date.now(),
  });
}

export function saveZoteroPaperLink(
  paperId: string | number,
  link: ZoteroPaperLink,
) {
  if (typeof window === "undefined" || !isZoteroPaperLink(link)) return false;
  const normalizedPaperId = String(paperId).trim();
  if (!normalizedPaperId) return false;
  const links = readLinks();
  links[normalizedPaperId] = link;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(links));
  } catch {
    return false;
  }
  try {
    window.dispatchEvent(new CustomEvent(LINKS_CHANGED_EVENT, {
      detail: { paperId: normalizedPaperId },
    }));
  } catch {
    // The link is already persisted; a failed same-window refresh must not break upload completion.
  }
  return true;
}

export function getZoteroPaperLink(paperId: string | number) {
  return readLinks()[String(paperId)] ?? null;
}

export function getZoteroPaperLinkForHashes(
  paperId: string | number,
  md5: unknown,
  sha1: unknown,
) {
  const link = getZoteroPaperLink(paperId);
  if (!link) return null;

  // Older list responses may not include hashes. Keep the explicit import link unless
  // the current paper provides enough evidence that it points at different content.
  if (!isHash(md5, 32) || !isHash(sha1, 40)) {
    return link;
  }
  return link.md5.toLowerCase() === md5.toLowerCase()
    && link.sha1.toLowerCase() === sha1.toLowerCase()
    ? link
    : null;
}

export function subscribeZoteroPaperLinks(listener: () => void) {
  if (typeof window === "undefined") return () => undefined;
  window.addEventListener(LINKS_CHANGED_EVENT, listener);
  window.addEventListener("storage", listener);
  return () => {
    window.removeEventListener(LINKS_CHANGED_EVENT, listener);
    window.removeEventListener("storage", listener);
  };
}
